import { DotsVerticalIcon } from '@heroicons/react/outline';
import classNames from 'classnames';
import { useAtom } from 'jotai';
import { memo, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { IconSvg } from '@/lib/v2/components/IconSvg';
import { If } from '@/lib/v2/components/If';
import { Text } from '@/lib/v2/components/Text';
import { LoadingIcon } from '@/lib/v2/icons/animated';
import { EmptyImageIcon } from '@/lib/v2/icons/general';
import { ActionCopyIcon, ClipboardIcon, EmojiIcon, OpenNewWindow } from '@/lib/v2/icons/outline';
import {
  ArrowIcon,
  CameraIcon,
  MicrophoneIcon,
  PhoneSimpleIcon,
  QuickReplyIcon,
  UserIcon,
} from '@/lib/v2/icons/solid';
import { PdfIcon } from '@/lib/v2/icons/solid/PdfIcon';

import { WhatsAppBackground } from './WhatsAppBackground';

import './PhonePreviewWhatsApp.tailwind.css';

import { atomCurrentWhatsAppTemplate } from '@/modules/CampaignsModule/atoms/actionWhatsAppAtom';
import { useConvertBodyVariables } from '@/modules/CampaignsModule/components/actionManagement/WhatsAppAction/hooks/useExtractBodyFromJson';
import { TRANSLATE_PHONE_PREVIEW } from '@/modules/CampaignsModule/constants';
import {
  IWhatsAppTemplate,
  TemplateKey,
  TwilioMediaTemplate,
  WHATSAPP_ACTION_TYPES,
  WhatsAppCardTemplate,
} from '@/modules/CampaignsModule/interfaces/WhatsApp';

export interface IPhonePreviewWhatsAppProps {
  isShortPhone?: boolean;
  isLoading?: boolean;
  initialTemplate?: IWhatsAppTemplate;
}

const PhonePreviewWhatsAppV0 = ({
  isShortPhone = false,
  isLoading = false,
  initialTemplate,
}: IPhonePreviewWhatsAppProps): JSX.Element => {
  const { t } = useTranslation('inbox');

  const [currentWhatsAppTemplate, setCurrentWhatsAppTemplate] = useAtom(
    atomCurrentWhatsAppTemplate
  );

  useEffect(() => {
    if (initialTemplate) {
      setCurrentWhatsAppTemplate(initialTemplate);
    }
  }, [initialTemplate, setCurrentWhatsAppTemplate]);

  const templateType = Object.keys(currentWhatsAppTemplate?.types || {})?.[0] as TemplateKey;

  const hasMedia = !!(currentWhatsAppTemplate?.types?.[templateType] as TwilioMediaTemplate)
    ?.media?.[0];

  const header = (currentWhatsAppTemplate?.types?.[templateType] as WhatsAppCardTemplate)
    ?.header_text;

  const message = currentWhatsAppTemplate?.types?.[templateType]?.body;
  const bodyMessage = useConvertBodyVariables(message ?? '', currentWhatsAppTemplate?.variables);

  const mediaUrl = (currentWhatsAppTemplate?.types?.[templateType] as TwilioMediaTemplate)
    ?.media?.[0];
  const isPdfFile = mediaUrl?.toLowerCase().endsWith('.pdf');
  const [mediaError, setMediaError] = useState(false);

  useEffect(() => {
    if (mediaUrl) {
      setMediaError(false);
    }
  }, [mediaUrl]);

  const footer = (currentWhatsAppTemplate?.types?.[templateType] as WhatsAppCardTemplate)?.footer;

  const actions = (currentWhatsAppTemplate?.types?.[templateType] as WhatsAppCardTemplate)?.actions;

  const getButtonIcon = (buttonType: string) => {
    switch (buttonType) {
      case WHATSAPP_ACTION_TYPES.URL:
        return <OpenNewWindow />;
      case WHATSAPP_ACTION_TYPES.COPY_CODE:
        return <ActionCopyIcon />;
      case WHATSAPP_ACTION_TYPES.PHONE_NUMBER:
      case WHATSAPP_ACTION_TYPES.VOICE_CALL:
        return <PhoneSimpleIcon />;
      case WHATSAPP_ACTION_TYPES.QUICK_REPLY:
        return <QuickReplyIcon />;
      default:
        return null;
    }
  };

  const classesContainer = classNames(
    'relative h-full max-h-[600px] w-[310px] overflow-hidden border-[#054D47] bg-[#054D47]',
    {
      'rounded-t-[30px] border-x-[6px] border-b-0 border-t-[6px]': isShortPhone,
      'rounded-[30px] border-[6px]': !isShortPhone,
    }
  );
  const classesMessage = classNames(
    'relative mb-2 z-10 min-h-[118px] break-words rounded-xl bg-emblue-green-mint text-left leading-tight text-[#364365] whitespace-pre-wrap',
    { 'flex justify-center items-center': isLoading },
    { 'p-1': hasMedia, 'p-4': !hasMedia }
  );
  const classesText = classNames('', {
    'px-2 pt-2': hasMedia,
  });
  const classesFooter = classNames('text-xs pb-1 pt-2 italic text-gray-500', {
    'px-2 ': hasMedia,
  });
  const classesDivider = classNames('h-0 border-t border-emblue-disabled/50', {
    'mx-[-3px]': hasMedia,
    'mx-[-15px]': !hasMedia,
  });

  const scrollToBottom = (el: HTMLDivElement | null) => {
    if (el) {
      el.scrollTop = el.scrollHeight;
    }
  };

  useEffect(() => {
    return () => {
      setCurrentWhatsAppTemplate({
        to: '',
        from: '',
        ContentSid: '',
        friendly_name: '',
        language: '',
        variables: {},
        types: {
          'twilio/text': {
            body: '',
          },
        },
      });
    };
  }, [setCurrentWhatsAppTemplate]);

  return (
    <div className="flex h-full items-center" data-testid="phone-preview-component">
      <div className={classesContainer}>
        <div className="relative z-20 flex flex-col items-center gap-2 bg-emblue-green-dark pb-3 pt-[50px]">
          <div className="absolute inset-x-0 top-0 mx-auto h-[38px] w-[210px] rounded-b-lg bg-[#054D47]" />
          <div className="flex w-full justify-between px-4">
            <div className="flex items-center gap-2">
              <IconSvg height="16px" svgComponent={<ArrowIcon />} width="16px" />
              <IconSvg
                rounded
                bgColor="gray-light"
                fillColor="gray-dark"
                height="32px"
                svgComponent={<UserIcon />}
                width="32px"
                withPadding="small"
              />
              <div className="h-[11px] w-[92px] rounded-md bg-emblue-gray-light-preview" />
            </div>
            <div className="flex items-center gap-4">
              <IconSvg
                fillColor="white"
                height="20px"
                svgComponent={<PhoneSimpleIcon />}
                width="20px"
              />
              <IconSvg
                height="20px"
                strokeColor="white"
                svgComponent={<DotsVerticalIcon />}
                width="20px"
              />
            </div>
          </div>
        </div>
        <div className="relative h-[calc(100%-97px)]">
          <div className="absolute inset-0">
            <WhatsAppBackground />
          </div>
          <div
            ref={scrollToBottom}
            className="visible-scrollbar relative z-10 h-[calc(100%-60px)] overflow-y-auto pl-5 pr-2 pt-5"
          >
            <div className={classesMessage}>
              <If condition={isLoading}>
                <LoadingIcon />
              </If>
              <If condition={!isLoading && !!header}>
                <div className="py-2">
                  <Text fontWeight="medium" variant="headline">
                    {header}
                  </Text>
                </div>
              </If>
              <If condition={hasMedia}>
                <If condition={mediaError}>
                  <IconSvg fullWidth svgComponent={<EmptyImageIcon />} withPadding="small" />
                </If>
                <If condition={!mediaError}>
                  <If condition={isPdfFile}>
                    <div className="relative flex size-full items-center rounded-lg bg-black/10 p-3 pb-5">
                      <div className="mr-3 flex min-h-10 min-w-10 items-center justify-center overflow-hidden">
                        <div className="relative flex size-8 items-center justify-center">
                          <IconSvg height="30px" svgComponent={<PdfIcon />} width="30px" />
                        </div>
                      </div>
                      <div className="flex flex-1 flex-col overflow-hidden">
                        <Text className="truncate font-medium" variant="text-sm">
                          {mediaUrl?.split('/').pop() || 'document.pdf'}
                        </Text>
                      </div>
                    </div>
                  </If>
                  <If condition={mediaUrl?.endsWith('.mp4')}>
                    <video
                      controls
                      className="size-full overflow-hidden rounded-lg border-0 object-contain"
                      src={mediaUrl}
                      onError={() => setMediaError(true)}
                    >
                      <track kind="captions" />
                    </video>
                  </If>
                  <If condition={!isPdfFile && !mediaUrl?.endsWith('.mp4')}>
                    <img
                      alt="media"
                      className="size-full overflow-hidden rounded-lg border-0 bg-black object-contain"
                      loading="lazy"
                      src={mediaUrl}
                      onError={() => setMediaError(true)}
                    />
                  </If>
                </If>
              </If>
              <If condition={!isLoading && !!message}>
                <div
                  dangerouslySetInnerHTML={{ __html: bodyMessage || '' }}
                  className={classesText}
                />
              </If>
              <If condition={!isLoading && !!footer}>
                <div className={classesFooter}>{footer}</div>
              </If>
              <If condition={!isLoading && !!actions && actions.length > 0}>
                <div className="mt-2 pt-1">
                  <div className={classesDivider}></div>
                  {actions?.map((button, index) => (
                    <div
                      key={`${button.type}-${button.title}`}
                      className={`flex items-center justify-center gap-2 px-4 py-2 text-center text-base font-medium text-emblue-primary ${
                        index !== actions.length - 1 ? 'border-b border-white/10' : ''
                      }`}
                    >
                      {button.type && getButtonIcon(button.type) && (
                        <IconSvg
                          height="16px"
                          svgComponent={getButtonIcon(button.type)}
                          width="16px"
                        />
                      )}
                      {button.title}
                    </div>
                  ))}
                </div>
              </If>
            </div>
          </div>
          <If condition={!isShortPhone}>
            <div className="absolute inset-x-0 bottom-0 z-20 p-2">
              <div className="flex items-center justify-between">
                <div className="flex h-9 w-[245px] justify-between rounded-full bg-white px-3 py-2">
                  <div className="flex items-center gap-1">
                    <IconSvg
                      fillColor="gray"
                      height="18px"
                      svgComponent={<EmojiIcon />}
                      width="18px"
                    />
                    <Text color="gray" variant="text-sm">
                      {t(`${TRANSLATE_PHONE_PREVIEW}.PLACEHOLDER`)}
                    </Text>
                  </div>
                  <div className="flex items-center gap-2">
                    <IconSvg
                      fillColor="gray"
                      height="18px"
                      svgComponent={<ClipboardIcon />}
                      width="18px"
                    />
                    <IconSvg
                      fillColor="gray"
                      height="18px"
                      svgComponent={<CameraIcon />}
                      width="18px"
                    />
                  </div>
                </div>

                <IconSvg
                  rounded
                  bgColor="greenTeal"
                  height="32px"
                  svgComponent={<MicrophoneIcon />}
                  width="32px"
                  withPadding="small"
                />
              </div>
            </div>
          </If>
        </div>
      </div>
    </div>
  );
};

export default memo(PhonePreviewWhatsAppV0);
